import { useEffect, useState } from "react";
import { Loader2, Upload, FileText, Trash2, Inbox } from "lucide-react";
import { toast } from "sonner";
import { MailboxList } from "./MailboxList";

const STATUS = {
  posted: { label: "COMPTABILISÉE", color: "#22c55e" },
  to_review: { label: "À VÉRIFIER", color: "#f97316" },
  duplicate: { label: "DOUBLON", color: "#a855f7" },
  ignored: { label: "IGNORÉE", color: "#64748b" },
  error: { label: "ERREUR", color: "#ef4444" },
  no_entry: { label: "SANS ÉCRITURE", color: "#f7c935" },
  no_attachment: { label: "SANS PIÈCE", color: "#64748b" },
  extracted: { label: "EXTRAITE", color: "#38bdf8" },
};

const fmt = (v) => `${Number(v || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €`;

function UploadZone({ api, societeId, onDone }) {
  const [busy, setBusy] = useState(false);
  const onFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("societe_id", societeId);
      const r = await api.post("/accounting/mailbox/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const d = r.data;
      const x = d.extraction || {};
      if (d.status === "posted") toast.success(`Facture ${x.supplier || ""} ${fmt(x.amount_ttc)} comptabilisée automatiquement`);
      else if (d.status === "to_review") toast.success(`Facture ${x.supplier || ""} extraite — écriture à vérifier`);
      else toast.error(d.error || `Statut : ${d.status}`);
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy(false);
  };
  return (
    <label
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
      className={`block cursor-pointer border-2 border-dashed border-white/10 rounded-lg p-5 text-center hover:border-[#f7c935]/40 transition-colors bg-[#0b0811] ${busy ? "opacity-60" : ""}`}
      data-testid="invoice-dropzone"
    >
      <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ""; }} data-testid="invoice-upload-input" disabled={busy} />
      {busy ? <><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#f7c935]" /><div className="text-xs text-slate-400 mt-2">Lecture IA de la facture…</div></> : (
        <>
          <Upload className="w-6 h-6 text-slate-500 mx-auto mb-1" />
          <div className="text-xs text-slate-300 font-semibold">Déposer une facture fournisseur</div>
          <div className="text-[10px] text-slate-500">PDF ou photo · extraction IA → dépense → écriture automatique</div>
        </>
      )}
    </label>
  );
}

export function MailboxTab({ api, societeId, onChanged }) {
  const [docs, setDocs] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const load = async () => {
    try { const r = await api.get("/accounting/mailbox/documents", { params: { societe_id: societeId, status: filter || undefined } }); setDocs(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setLoading(false);
  };
  useEffect(() => { setLoading(true); load(); /* eslint-disable-next-line */ }, [societeId, filter]);
  const refresh = () => { load(); onChanged?.(); };
  const del = async (d) => {
    if (!window.confirm("Retirer ce document de l'historique ? (la dépense et l'écriture sont conservées)")) return;
    try { await api.delete(`/accounting/mailbox/documents/${d.doc_id}`); load(); } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="mailbox-tab">
      <div className="lg:col-span-1 space-y-3">
        <MailboxList api={api} societeId={societeId} onChanged={refresh} />
        <UploadZone api={api} societeId={societeId} onDone={refresh} />
      </div>
      <div className="lg:col-span-2 bg-[#0b0811] border border-white/5 rounded-lg p-3">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-300 flex items-center gap-1.5"><Inbox className="w-3.5 h-3.5 text-[#38bdf8]" /> Factures reçues ({docs.length})</h4>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="bg-[#14101c] border border-white/10 text-white rounded px-2 h-8 text-xs" data-testid="mailbox-docs-filter">
            <option value="">Tous statuts</option>
            {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto my-8 text-[#f7c935]" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-slate-400"><tr>
                <th className="text-left px-2 py-1 font-bold">Reçue</th><th className="text-left px-2 py-1 font-bold">Fournisseur / pièce</th>
                <th className="text-left px-2 py-1 font-bold">N° facture</th><th className="text-right px-2 py-1 font-bold">TTC</th>
                <th className="text-center px-2 py-1 font-bold">Statut</th><th></th>
              </tr></thead>
              <tbody>
                {docs.map((d) => {
                  const s = STATUS[d.status] || STATUS.extracted;
                  const x = d.extraction || {};
                  return (
                    <tr key={d.doc_id} className="border-t border-white/5" data-testid={`mail-doc-${d.doc_id}`}>
                      <td className="px-2 py-1.5 text-slate-400 whitespace-nowrap">{(d.created_at || "").slice(0, 10)}</td>
                      <td className="px-2 py-1.5">
                        <div className="text-white font-medium truncate max-w-[220px]">{x.supplier || d.subject || "—"}</div>
                        <div className="text-[10px] text-slate-500 truncate max-w-[220px] flex items-center gap-1"><FileText className="w-3 h-3" /> {d.filename || "aucune pièce"}{d.from_email ? ` · ${d.from_email}` : ""}</div>
                        {d.error && <div className="text-[10px] text-amber-300 truncate max-w-[220px]">{d.error}</div>}
                      </td>
                      <td className="px-2 py-1.5 font-mono text-slate-300">{x.invoice_number || ""}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-slate-200">{x.amount_ttc ? fmt(x.amount_ttc) : ""}</td>
                      <td className="px-2 py-1.5 text-center"><span className="text-[9px] px-1.5 py-0.5 rounded font-black" style={{ background: `${s.color}20`, color: s.color }} data-testid={`mail-doc-status-${d.doc_id}`}>{s.label}</span></td>
                      <td className="px-2 py-1.5 text-right"><button onClick={() => del(d)} className="text-red-400 hover:text-red-300 p-1" data-testid={`mail-doc-delete-${d.doc_id}`}><Trash2 className="w-3.5 h-3.5" /></button></td>
                    </tr>
                  );
                })}
                {docs.length === 0 && <tr><td colSpan="6" className="text-center py-10 text-slate-500">Aucune facture reçue pour l'instant. Connectez une boîte mail ou déposez un PDF.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
